// ./src/components/features/Logbook/LogbookEditDrawer/types.ts
//
// Shared types, form data interface, and helpers for the unified
// LogbookEditDrawer that handles Refuels, Expenses, and Checkpoints.
//
// NOTE: Location fields (whereDone, location, latitude, longitude) have been
// moved to the usePlaceLocation hook. They are no longer part of FormData.

import { UploadedFile } from "@/utils/api";
import { getCurrentLocalDateTimeString } from "@/utils";
import { EXPENSE_TYPES } from "@/config";

// ============================================
// Constants
// ============================================

/** Expense kind IDs that should show the tire set selector */
export const TIRE_EXPENSE_KIND = {
  TIRE_REPAIR_REPLACEMENT: 20,
  SEASONAL_TIRE_SERVICE: 21,
  TIRE_BALANCE: 22,
  TIRE_ROTATION: 23,
} as const;

export const TIRE_RELATED_KIND_IDS = new Set<number>(
  Object.values(TIRE_EXPENSE_KIND)
);

export type TankType = "main" | "addl";
export const TANK_TYPE_MAIN: TankType = "main";
export const TANK_TYPE_ADDL: TankType = "addl";

export type AmountMode = "total" | "breakdown";
export type PriceCalcMode = "total-first" | "unit-first";

/** Record type options for the type selector */
export const LOGBOOK_RECORD_TYPES = [
  EXPENSE_TYPES.REFUEL,
  EXPENSE_TYPES.EXPENSE,
  EXPENSE_TYPES.CHECKPOINT,
] as const;

export type LogbookRecordType = (typeof LOGBOOK_RECORD_TYPES)[number];

// ============================================
// Known form fields (for ApiError field extraction)
// ============================================

export const KNOWN_FORM_FIELDS = [
  "carId",
  "whenDone",
  "odometer",
  "expenseType",
  // Refuel
  "tankType",
  "refuelVolume",
  "volumeEnteredIn",
  "totalPrice",
  "pricePerVolume",
  "fuelGrade",
  "paidInCurrency",
  "totalPriceInHc",
  "homeCurrency",
  // Expense
  "kindId",
  "shortNote",
  "costWork",
  "costParts",
  "tax",
  "fees",
  "tireSetId",
  // Tank level (shared)
  "fuelInTank",
  "isFullTank",
  // Location (managed by usePlaceLocation, but errors still come from API)
  "location",
  "whereDone",
  "latitude",
  "longitude",
  "savedPlaceId",
  "savePlace",
  // Additional
  "comments",
  "travelId",
  "tags",
  "uploadedFilesIds",
];

// ============================================
// Unified FormData
// ============================================

export interface FormData {
  // ── Record Type ──
  expenseType: number;

  // ── Vehicle & Date (shared) ──
  carId: string | null;
  whenDone: string | null;
  odometer: number | null;

  // ── Refuel-specific ──
  tankType: TankType;
  refuelVolume: number | null;
  volumeEnteredIn: string;
  totalPrice: number | null;
  pricePerVolume: number | null;
  fuelGrade: string | null;

  // ── Expense-specific ──
  kindId: number | null;
  shortNote: string;
  amountMode: AmountMode;
  costWork: number | null;
  costParts: number | null;
  tax: number | null;
  fees: number | null;
  tireSetId: string | null;

  // ── Currency (refuel + expense) ──
  paidInCurrency: string;
  costWorkHc: number | null;
  costPartsHc: number | null;
  totalPriceInHc: number | null;
  homeCurrency: string;

  // ── Tank Level (shared — all three types) ──
  tankLevelPercent: number;
  tankLevelUnknown: boolean;

  // ── Location ──
  // NOTE: Location fields (whereDone, location, latitude, longitude) are now
  // managed by the usePlaceLocation hook. They are NOT part of FormData.

  // ── Additional (shared) ──
  comments: string;
  travelId: string | null;

  // ── Tags (shared) ──
  tagIds: string[];

  // ── Attachments (shared) ──
  uploadedFilesIds: string[];
}

// ============================================
// Expanded Sections State
// ============================================

export interface ExpandedSections {
  // Refuel-specific (no section — fields always visible)
  // Expense-specific (no section — fields always visible)
  tankLevel: boolean;
  travel: boolean;
  tags: boolean;
  additional: boolean;
  documents: boolean;
}

// ============================================
// Factory Functions
// ============================================

export const getInitialFormData = (
  currency: string,
  expenseType: number = EXPENSE_TYPES.REFUEL
): FormData => ({
  expenseType,
  carId: null,
  whenDone: getCurrentLocalDateTimeString(),
  odometer: null,
  // Refuel
  tankType: TANK_TYPE_MAIN,
  refuelVolume: null,
  volumeEnteredIn: "l",
  totalPrice: null,
  pricePerVolume: null,
  fuelGrade: null,
  // Expense
  kindId: null,
  shortNote: "",
  amountMode: "total",
  costWork: null,
  costParts: null,
  tax: null,
  fees: null,
  tireSetId: null,
  // Currency
  paidInCurrency: currency,
  costWorkHc: null,
  costPartsHc: null,
  totalPriceInHc: null,
  homeCurrency: currency,
  // Tank level
  tankLevelPercent: expenseType === EXPENSE_TYPES.REFUEL ? 100 : 50,
  tankLevelUnknown: expenseType !== EXPENSE_TYPES.REFUEL,
  // Additional
  comments: "",
  travelId: null,
  tagIds: [],
  uploadedFilesIds: [],
});

export const getInitialExpandedSections = (
  expenseType: number = EXPENSE_TYPES.REFUEL
): ExpandedSections => ({
  tankLevel: expenseType === EXPENSE_TYPES.REFUEL,
  travel: false,
  tags: false,
  additional: false,
  documents: false,
});

// ============================================
// Helpers
// ============================================

/**
 * Convert fuelInTank (0.0–1.0) to slider value (0–100).
 */
export const fuelInTankToSliderValue = (
  fuelInTank: number | null | undefined
): number => {
  if (fuelInTank == null) return 50;
  return Math.round(fuelInTank * 100);
};

/**
 * Serialize form + files into a stable string for dirty-state comparison.
 */
export const serializeFormSnapshot = (
  data: FormData,
  files: UploadedFile[]
): string => {
  return JSON.stringify({
    ...data,
    _fileIds: files.map((f) => f.id).sort(),
  });
};

/**
 * Check if the given kindId is tire-related.
 */
export const isTireRelatedKindId = (kindId: number | null): boolean => {
  return kindId != null && TIRE_RELATED_KIND_IDS.has(kindId);
};